/**
 * @module components/layout/Header
 * @description Site header with desktop and mobile layouts: brand mark,
 * analyses-remaining badge, manage-subscription shortcut, Google sign-in,
 * and the Product Hunt badge (desktop only).
 *
 * @extracted-from client/src/App.js (lines ~972-1122)
 */

import React from 'react';
import { CreditCard } from 'lucide-react';
import AnalysesRemainingBadge from './AnalysesRemainingBadge';
import GoogleSignup from '../auth/GoogleSignup';

/**
 * @param {Object} props
 * @param {number} props.totalAnalysesRemaining - Analyses left for this account.
 * @param {number} props.analysesPercent - Meter fill percentage (0-100) for the analyses badge.
 * @param {number} props.analysesMeterMax - Meter max (plan limit, free allotment, or paid high-water).
 * @param {() => void} props.onPurchaseClick - Called when "Purchase analyses" is clicked.
 * @param {(userData: Object|null) => void} props.onAuthChange - Forwarded to `GoogleSignup` on sign-in/out.
 * @param {boolean} [props.showManageSubscription=false] - Whether to render the "Manage subscription" shortcut.
 * @param {() => void} [props.onManageSubscription] - Called when "Manage subscription" is clicked.
 * @param {Object|null} [props.user] - Signed-in user, used to vary the manage-subscription button label/aria-label.
 */
const Header = ({
  totalAnalysesRemaining,
  analysesPercent,
  analysesMeterMax,
  onPurchaseClick,
  onAuthChange,
  showManageSubscription = false,
  onManageSubscription,
  user,
}) => {
  return (
    <header className="relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between h-24 gap-4">
          <div className="flex items-center space-x-6 flex-shrink-0">
            <div>
              <h1 className="font-display text-4xl font-bold text-gradient-blue tracking-tight">CriticAI</h1>
              <p className="text-sm text-critic-text-muted font-normal tracking-wide">Your Personal Critic</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 flex-shrink-0">
            <AnalysesRemainingBadge
              totalAnalysesRemaining={totalAnalysesRemaining}
              analysesPercent={analysesPercent}
              analysesMeterMax={analysesMeterMax}
              onPurchaseClick={onPurchaseClick}
              variant="desktop"
            />
            {showManageSubscription && (
              <button
                type="button"
                onClick={() => onManageSubscription && onManageSubscription()}
                className="hidden md:inline-flex items-center gap-1.5 shrink-0 rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 text-xs font-medium text-white/75 transition-colors hover:border-white/25 hover:bg-white/[0.07] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-apple-blue/55 focus-visible:ring-offset-2 focus-visible:ring-offset-critic-bg-deep"
                aria-label={
                  user
                    ? 'Manage subscription in Stripe'
                    : 'Sign in to manage subscription'
                }
              >
                <CreditCard className="h-3.5 w-3.5 opacity-90" aria-hidden />
                {user ? 'Manage subscription' : 'Sign in to manage'}
              </button>
            )}
            <GoogleSignup onAuthChange={onAuthChange} />
            {/* Product Hunt Badge - Desktop */}
            <a
              href="https://www.producthunt.com/products/criticai?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-criticai"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              <img
                src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1001146&theme=light&t=1754850638651"
                alt="CriticAI - Analyze Your Craft | Product Hunt"
                style={{ width: '200px', height: '43px' }}
                width="200"
                height="43"
              />
            </a>
          </div>
        </div>

        {/* Enhanced Mobile Layout - Centered with Better Spacing */}
        <div className="md:hidden flex flex-col items-center justify-center min-h-32 py-4 space-y-3">
          <div className="text-center">
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-gradient-blue tracking-tight mb-1">CriticAI</h1>
            <p className="text-sm text-critic-text-muted font-normal tracking-wide">Your Personal Critic</p>
          </div>

          {/* Analyses Counter - Mobile (Centered) */}
          <div className="flex flex-col items-center space-y-3">
            <AnalysesRemainingBadge
              totalAnalysesRemaining={totalAnalysesRemaining}
              analysesPercent={analysesPercent}
              analysesMeterMax={analysesMeterMax}
              onPurchaseClick={onPurchaseClick}
              variant="mobile"
            />
            {showManageSubscription && (
              <button
                type="button"
                onClick={() => onManageSubscription && onManageSubscription()}
                className="md:hidden inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 text-xs font-medium text-critic-text-secondary transition-colors hover:border-white/25 hover:bg-white/[0.07] hover:text-critic-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-apple-blue/55 focus-visible:ring-offset-2 focus-visible:ring-offset-critic-bg-deep"
                aria-label={
                  user
                    ? 'Manage subscription in Stripe'
                    : 'Sign in to manage subscription'
                }
              >
                <CreditCard className="h-3.5 w-3.5 opacity-90" aria-hidden />
                {user ? 'Manage subscription' : 'Sign in to manage'}
              </button>
            )}
            <GoogleSignup onAuthChange={onAuthChange} />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
